import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  BookOpen,
  HelpCircle,
  Home,
  LayoutDashboard,
  LogOut,
  Sparkles,
  User,
  Users,
  X,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { BRAND_NAME } from '../../config/brand';

const ease = 'duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none';

const Navbar: React.FC = () => {
  const { isAuthenticated, logout, currentUser } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mounted, setMounted] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const isHomePage = location.pathname === '/';
  const isOverlay = isHomePage && !isScrolled;

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 24);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const scrollToSection = useCallback(
    (sectionId: string) => {
      const section = document.getElementById(sectionId);
      if (section && location.pathname === '/') {
        section.scrollIntoView({ behavior: 'smooth' });
        return;
      }
      if (location.pathname !== '/') {
        window.location.href = `/#${sectionId}`;
        return;
      }
      navigate('/');
      setTimeout(() => {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
      }, 120);
    },
    [location.pathname, navigate]
  );

  const isCurrentPath = (path: string, matchPrefix = false) =>
    matchPrefix ? location.pathname.startsWith(path) : location.pathname === path;

  const accentBar = isOverlay ? 'bg-brand-400' : 'bg-brand-500';

  const navLinkClass = (active: boolean) =>
    `relative inline-flex min-h-[44px] items-center whitespace-nowrap rounded-full px-3 text-sm font-semibold transition-[color,background-color,border-color,box-shadow,opacity] ${ease} focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300 focus-visible:ring-offset-2 ${
      isOverlay
        ? active
          ? 'text-white'
          : 'text-brand-200 hover:bg-white/10 hover:text-white'
        : active
          ? 'text-brand-800'
          : 'text-slate-600 hover:bg-brand-50 hover:text-brand-800'
    }`;

  const mobileLinkClass = (active: boolean) =>
    `flex w-full items-center gap-3 rounded-xl px-4 py-3.5 text-sm font-semibold transition-all ${
      active
        ? 'bg-brand-600 text-white shadow-md'
        : 'text-slate-700 hover:bg-brand-50 hover:text-brand-800'
    }`;

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50" dir="rtl">
        <div
          className={`transition-[background-color,border-color,box-shadow,backdrop-filter] ${ease} ${
            isOverlay
              ? 'border-b border-white/10 bg-brand-800/75 backdrop-blur-md'
              : 'border-b border-brand-100 bg-white/95 shadow-md backdrop-blur-xl'
          }`}
        >
          <div
            className={`mx-auto flex w-full max-w-7xl items-center justify-between gap-3 px-4 transition-[min-height,padding] ${ease} sm:px-6 lg:grid lg:grid-cols-[minmax(180px,1fr)_auto_minmax(180px,1fr)] lg:gap-4 lg:px-7 ${
              isOverlay ? 'min-h-[4.75rem]' : 'min-h-16 sm:min-h-[4.25rem]'
            }`}
          >
            <div className="order-2 justify-self-start lg:order-none">
              <Link
                to="/"
                className="flex min-h-[44px] min-w-0 shrink-0 items-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300 focus-visible:ring-offset-2"
                aria-label={`${BRAND_NAME} — الصفحة الرئيسية`}
              >
                <span
                  className={`me-[0.55rem] inline-block h-[1.35rem] w-[0.2rem] rounded-full ${accentBar}`}
                  aria-hidden
                />
                <span
                  className={`inline-flex items-center justify-center rounded-2xl border px-2.5 py-1.5 transition-[background-color,border-color,box-shadow] ${ease} ${
                    isOverlay
                      ? 'border-white/15 bg-white/95 shadow-[0_4px_16px_rgba(7,11,24,0.35)]'
                      : 'border-brand-100 bg-slate-50 shadow-[0_4px_16px_rgba(22,32,64,0.12)]'
                  }`}
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/favicon.ico`}
                    alt={BRAND_NAME}
                    className="h-9 w-auto object-contain sm:h-10"
                    width={120}
                    height={40}
                    decoding="async"
                  />
                </span>
              </Link>
            </div>

            <nav
              className="order-3 hidden items-center justify-self-center gap-1 lg:order-none lg:flex"
              aria-label="التنقل الرئيسي"
            >
              <Link
                to="/"
                className={navLinkClass(isCurrentPath('/'))}
                aria-current={isCurrentPath('/') ? 'page' : undefined}
              >
                الرئيسية
                <span
                  className={`absolute inset-x-[0.85rem] bottom-[0.35rem] h-0.5 rounded-full transition-opacity duration-500 ${accentBar} ${
                    isCurrentPath('/') ? 'opacity-100' : 'opacity-0'
                  }`}
                  aria-hidden
                />
              </Link>
              <button
                type="button"
                onClick={() => scrollToSection('suggested-courses-section')}
                className={navLinkClass(false)}
              >
                الكورسات
                <span
                  className={`absolute inset-x-[0.85rem] bottom-[0.35rem] h-0.5 rounded-full opacity-0 ${accentBar}`}
                  aria-hidden
                />
              </button>
              <button
                type="button"
                onClick={() => scrollToSection('grades-section')}
                className={navLinkClass(false)}
              >
                الصفوف
                <span
                  className={`absolute inset-x-[0.85rem] bottom-[0.35rem] h-0.5 rounded-full opacity-0 ${accentBar}`}
                  aria-hidden
                />
              </button>
              <button
                type="button"
                onClick={() => scrollToSection('faq-section')}
                className={navLinkClass(false)}
              >
                الأسئلة الشائعة
                <span
                  className={`absolute inset-x-[0.85rem] bottom-[0.35rem] h-0.5 rounded-full opacity-0 ${accentBar}`}
                  aria-hidden
                />
              </button>
              {mounted && isAuthenticated && (
                <Link
                  to="/student-portal"
                  className={navLinkClass(isCurrentPath('/student-portal', true))}
                >
                  بوابة الطالب
                  <span
                    className={`absolute inset-x-[0.85rem] bottom-[0.35rem] h-0.5 rounded-full transition-opacity duration-500 ${accentBar} ${
                      isCurrentPath('/student-portal', true) ? 'opacity-100' : 'opacity-0'
                    }`}
                    aria-hidden
                  />
                </Link>
              )}
            </nav>

            <div className="order-4 hidden items-center justify-end justify-self-end gap-2 lg:order-none lg:flex">
              {mounted && isAuthenticated ? (
                <>
                  <div className="flex max-w-[150px] items-center gap-2 rounded-full border border-brand-100 bg-brand-50/80 px-2 py-1">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-600">
                      <User className="h-4 w-4 text-white" />
                    </div>
                    <span className="truncate text-xs font-semibold text-slate-800 sm:text-sm">
                      {currentUser?.name}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={logout}
                    className={`inline-flex min-h-12 items-center justify-center gap-1.5 rounded-2xl border border-brand-100 px-[1.1rem] text-sm font-semibold text-slate-900 transition-[color,background-color,border-color,box-shadow] hover:border-red-200 hover:bg-red-50 hover:text-red-600 ${ease}`}
                  >
                    <LogOut className="h-4 w-4" />
                    خروج
                  </button>
                  <Link
                    to="/student-portal"
                    className={`inline-flex min-h-12 items-center justify-center gap-1.5 rounded-2xl border border-brand-600 bg-brand-600 px-[1.1rem] text-sm font-semibold text-white transition hover:bg-brand-700 ${ease}`}
                  >
                    <LayoutDashboard className="h-4 w-4" />
                    البوابة
                  </Link>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className={`inline-flex min-h-12 items-center justify-center whitespace-nowrap rounded-2xl border px-[1.1rem] text-sm font-semibold transition-[color,background-color,border-color,box-shadow] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2 ${ease} ${
                      isOverlay
                        ? 'border-white/25 bg-white/10 text-white hover:bg-white/15'
                        : 'border-brand-200 bg-slate-50 text-brand-800 hover:bg-brand-50'
                    }`}
                  >
                    تسجيل الدخول
                  </Link>
                  <Link
                    to="/register"
                  className={`inline-flex min-h-12 items-center justify-center whitespace-nowrap rounded-2xl border px-[1.1rem] text-sm font-display transition focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 ${ease} ${
                      isOverlay
                        ? 'border-[var(--color-black)] bg-[var(--color-primary)] text-[var(--color-on-primary)] hover:bg-[var(--color-primary-dim)]'
                        : 'border-[var(--color-black)] bg-[var(--color-primary)] text-[var(--color-on-primary)] hover:bg-[var(--color-primary-dim)]'
                    }`}
                  >
                    إنشاء حساب
                  </Link>
                </>
              )}
            </div>

            <button
              type="button"
              onClick={() => setIsMobileMenuOpen((open) => !open)}
              className={`order-1 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border transition-[color,background-color,border-color,box-shadow] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300 lg:order-none lg:hidden ${ease} ${
                isOverlay
                  ? 'border-white/20 bg-white/10 text-white hover:bg-white/15'
                  : 'border-brand-100 bg-white text-brand-800 hover:border-brand-400 hover:bg-brand-50'
              }`}
              aria-label={isMobileMenuOpen ? 'إغلاق القائمة' : 'فتح القائمة'}
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <span aria-hidden className="flex flex-col gap-1.5">
                  <span className="block h-0.5 w-5 rounded-full bg-current" />
                  <span className="block h-0.5 w-5 rounded-full bg-current" />
                  <span className="block h-0.5 w-4 rounded-full bg-current" />
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" dir="rtl">
          <button
            type="button"
            className="absolute inset-0 bg-slate-900/30 backdrop-blur-sm"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-label="إغلاق القائمة"
          />
          <div className="absolute inset-x-3 top-[4.75rem] max-h-[calc(100vh-6rem)] overflow-y-auto rounded-2xl border border-brand-100 bg-white shadow-2xl sm:inset-x-4">
            <div className="border-b border-brand-100 px-4 py-3">
              <p className="text-xs font-semibold text-brand-600">القائمة</p>
              <p className="text-sm font-bold text-slate-900">
                {mounted && isAuthenticated ? 'بوابة الطالب' : 'استكشف المنصة'}
              </p>
            </div>

            <div className="space-y-1 p-3">
              <Link
                to="/"
                onClick={() => setIsMobileMenuOpen(false)}
                className={mobileLinkClass(isCurrentPath('/'))}
              >
                <Home className="h-5 w-5 shrink-0" />
                الرئيسية
              </Link>
              <button
                type="button"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  scrollToSection('suggested-courses-section');
                }}
                className={mobileLinkClass(false)}
              >
                <BookOpen className="h-5 w-5 shrink-0" />
                الكورسات
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  scrollToSection('grades-section');
                }}
                className={mobileLinkClass(false)}
              >
                <Sparkles className="h-5 w-5 shrink-0" />
                الصفوف
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  scrollToSection('faq-section');
                }}
                className={mobileLinkClass(false)}
              >
                <HelpCircle className="h-5 w-5 shrink-0" />
                الأسئلة الشائعة
              </button>
              <Link
                to="/parent-monitoring"
                onClick={() => setIsMobileMenuOpen(false)}
                className={mobileLinkClass(isCurrentPath('/parent-monitoring'))}
              >
                <Users className="h-5 w-5 shrink-0" />
                متابعة ولي الأمر
              </Link>
              {mounted && isAuthenticated && (
                <Link
                  to="/student-portal"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={mobileLinkClass(isCurrentPath('/student-portal', true))}
                >
                  <LayoutDashboard className="h-5 w-5 shrink-0" />
                  بوابة الطالب
                </Link>
              )}
            </div>

            <div className="space-y-2 border-t border-brand-100 p-3">
              {mounted && isAuthenticated ? (
                <button
                  type="button"
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    logout();
                  }}
                  className="flex min-h-11 w-full items-center justify-center gap-2 rounded-xl border border-red-200 px-4 py-3 text-sm font-semibold text-red-600"
                >
                  <LogOut className="h-4 w-4" />
                  تسجيل الخروج
                </button>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex min-h-11 items-center justify-center rounded-xl border border-brand-600 px-4 py-3 text-sm font-semibold text-slate-900"
                  >
                    تسجيل الدخول
                  </Link>
                  <Link
                    to="/register"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex min-h-11 items-center justify-center gap-2 rounded-xl bg-brand-600 px-4 py-3 text-sm font-semibold text-white"
                  >
                    <Sparkles className="h-4 w-4" />
                    إنشاء حساب
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
